/*
  Execution tab — pure helpers (UI overhaul wave 2, screen 09). No React, no API.
  Unit-tested in src/__tests__/project-detail-w2b-model.test.js.

  - formatExecutionTaskTitle / syncExecutionTaskHierarchy / getDescendantIds (moved from the page)
  - buildExecutionTree(tasks)                → children by parent, depth, root of every task
  - countExecutionChanges(saved, current)    → edited tasks vs the saved list (09 #6)
  - matchExecutionFilter / filterExecutionTasks / executionFilterOptions / executionViewCounts (09 #3);
    Done-by names compare by personKey (case / spaces); executionNamesForUser for "My tasks"
  - buildExecutionRows(…)                    → the rows on screen: open parents only (09 #9)
  - planBulkChange / applyBulkChange         → one change for many selected tasks + their sub-items (09 #4)
*/

const text = (value) => String(value ?? '').trim();
const plural = (count, word, many = `${word}s`) => `${count} ${count === 1 ? word : many}`;

export const EXECUTION_STATUS_OPTIONS = ['Not Started', 'In Progress', 'Done', 'Bottleneck', 'Not required', 'Update required', 'Waiting for Approval'];
export const EXECUTION_CATEGORY_OPTIONS = ['Execution', 'Drawings', 'Testing', 'Programming', 'Checking Prerequisites', 'Documentation', 'Installation', 'Material', 'Graphics', 'Manufacturing', 'WIR'];
export const EXECUTION_ASSIGNEES = [
  'Mohammed Elawady', 'Abd alrahman', 'Yahya Marei', 'Sobhy', 'Mohamed Hesham', 'Mostafa Mohammed', 'Vasim', 'Somar',
  'Abd ElAzim', 'Bahlol', 'Darwish', 'Hesham Mohsen', 'Amir Elsamak', 'Ali Eyad', 'Abdullah Ramzi', 'Shaikh rashith',
];
/** Fields a person edits on a task (what "N unsaved changes" compares). */
export const EXECUTION_EDIT_FIELDS = ['status', 'category', 'start_date', 'due_date', 'done_by', 'checked_by', 'comments', 'monitoring', 'zone', 'zone_2', 'zone_3', 'device_type'];
export const WAITING_STATUSES = ['Waiting for Approval', 'Update required'];
export const BLOCKED_STATUSES = ['Bottleneck'];

/* ------------------------------------------------------------------ hierarchy (moved from the page) */

export function formatExecutionTaskTitle(task, tasksOrById) {
  if (Number(task?.level || 1) === 1) return task?.title || '';
  const parent = tasksOrById instanceof Map
    ? tasksOrById.get(task.parent_id)
    : (tasksOrById || []).find((item) => item.id === task.parent_id);
  if (!parent?.title || !task?.title) return task?.title || '';
  const prefix = `${parent.title} - `;
  return task.title.startsWith(prefix) ? task.title.slice(prefix.length) : task.title;
}

/** All Done ⇒ parent Done; a parent marked Done marks its sub-items Done; one not Done ⇒ parent In Progress. */
export function syncExecutionTaskHierarchy(tasks, timestamp = new Date().toISOString()) {
  const next = tasks.map((task) => ({ ...task }));
  const childrenByParent = new Map();
  next.forEach((task) => {
    if (!task.parent_id) return;
    const children = childrenByParent.get(task.parent_id) || [];
    children.push(task);
    childrenByParent.set(task.parent_id, children);
  });
  const markDescendantsDone = (taskId) => {
    (childrenByParent.get(taskId) || []).forEach((child) => {
      if (child.status !== 'Done') {
        child.status = 'Done';
        child.updated = timestamp;
      }
      markDescendantsDone(child.id);
    });
  };
  next.forEach((task) => {
    if (task.status === 'Done') markDescendantsDone(task.id);
  });
  const tasksById = new Map(next.map((task) => [task.id, task]));
  const syncParentStatus = (taskId) => {
    const task = tasksById.get(taskId);
    if (!task) return;
    const children = childrenByParent.get(taskId) || [];
    if (children.length) {
      const allDone = children.every((child) => child.status === 'Done');
      if (allDone && task.status !== 'Done') {
        task.status = 'Done';
        task.updated = timestamp;
      } else if (!allDone && task.status === 'Done') {
        task.status = 'In Progress';
        task.updated = timestamp;
      }
    }
    if (task.parent_id) syncParentStatus(task.parent_id);
  };
  next.filter((task) => !(childrenByParent.get(task.id) || []).length).forEach((task) => syncParentStatus(task.id));
  return next;
}

/**
 * A sub-item that stops being Done reopens its Done parents (→ In Progress). Without this the hierarchy
 * sync (here and on the server) would mark it Done again because its parent is Done, and the edit would
 * silently not happen. Returns a new list; other tasks keep their object identity.
 */
export function reopenDoneAncestors(tasks, ids = [], timestamp = new Date().toISOString()) {
  const byId = new Map(tasks.map((task) => [task.id, task]));
  const reopen = new Set();
  ids.forEach((id) => {
    const seen = new Set();
    let parentId = byId.get(id)?.parent_id;
    while (parentId && byId.has(parentId) && !seen.has(parentId)) {
      seen.add(parentId);
      if (byId.get(parentId).status === 'Done') reopen.add(parentId);
      parentId = byId.get(parentId).parent_id;
    }
  });
  if (!reopen.size) return tasks;
  return tasks.map((task) => (reopen.has(task.id) ? { ...task, status: 'In Progress', updated: timestamp } : task));
}

export function getDescendantIds(tasks, taskId, childrenByParent = null) {
  const byParent = childrenByParent || buildExecutionTree(tasks).children;
  const ids = [];
  const stack = [...(byParent.get(taskId) || [])];
  while (stack.length) {
    const child = stack.pop();
    if (!child) continue;
    ids.push(child.id);
    stack.push(...(byParent.get(child.id) || []));
  }
  return ids;
}

/** → { byId, children: Map(parentId → tasks), roots, depth: Map(id → 0…), rootOf: Map(id → root task) } */
export function buildExecutionTree(tasks = []) {
  const byId = new Map();
  const children = new Map();
  (tasks || []).forEach((task) => {
    if (!task || !task.id) return;
    byId.set(task.id, task);
  });
  const roots = [];
  (tasks || []).forEach((task) => {
    if (!task || !task.id) return;
    if (task.parent_id && byId.has(task.parent_id)) {
      if (!children.has(task.parent_id)) children.set(task.parent_id, []);
      children.get(task.parent_id).push(task);
    } else {
      roots.push(task);
    }
  });
  const depth = new Map();
  const rootOf = new Map();
  const visit = (task, level, root) => {
    if (depth.has(task.id)) return; // cycles in broken data
    depth.set(task.id, level);
    rootOf.set(task.id, root);
    (children.get(task.id) || []).forEach((child) => visit(child, level + 1, root));
  };
  roots.forEach((task) => visit(task, 0, task));
  return { byId, children, roots, depth, rootOf };
}

/* ------------------------------------------------------------------ unsaved changes (09 #6) */

const same = (a, b) => {
  if (typeof a === 'boolean' || typeof b === 'boolean') return Boolean(a) === Boolean(b);
  return text(a) === text(b);
};

/** Tasks whose editable fields differ from the saved list → { count, ids, titles (first few) } */
export function countExecutionChanges(saved = [], current = []) {
  const savedById = new Map((saved || []).map((task) => [task?.id, task]));
  const ids = [];
  (current || []).forEach((task) => {
    const before = savedById.get(task?.id);
    if (!before) {
      ids.push(task.id);
      return;
    }
    if (before === task) return;
    if (EXECUTION_EDIT_FIELDS.some((field) => !same(before[field], task[field]))) ids.push(task.id);
  });
  return { count: ids.length, ids };
}

/* ------------------------------------------------------------------ filters + views (09 #3) */

export const EXECUTION_FILTER_DEFAULTS = {
  q: '',
  status: [],
  category: [],
  building: [],
  doneBy: [],
  monitoring: '',
  comments: false,
};

export const splitPeople = (value) => text(value).split(',').map((name) => name.trim()).filter(Boolean);
/** Done-by names are typed by hand: "Abd Alrahman", "Abd alrahman" and "abd  alrahman" are one person. */
export const personKey = (name) => text(name).toLowerCase().replace(/\s+/g, ' ');

/**
 * The Done-by names that belong to a signed-in user ("My tasks"): the same name in any case / spacing, or —
 * when the account carries a first name only — the one known Done-by name that starts with it
 * ("Yahya" → "Yahya Marei"). Two candidates ("Ali Eyad", "Ali Hassan") are ambiguous: no guess.
 */
export function executionNamesForUser(userName, knownNames = []) {
  const key = personKey(userName);
  if (!key) return [];
  const names = [...new Set((knownNames || []).map(text).filter(Boolean))];
  const same = names.filter((name) => personKey(name) === key);
  if (same.length) return same;
  const starting = names.filter((name) => personKey(name).startsWith(`${key} `));
  return starting.length === 1 ? starting : [text(userName)];
}

export function isExecutionFilterActive(filter = {}) {
  return Boolean(
    text(filter.q)
    || filter.status?.length || filter.category?.length || filter.building?.length || filter.doneBy?.length
    || filter.monitoring || filter.comments
  );
}

/** Does this one task match every set filter? (Search looks at title, category, status, zones, device type, comments.) */
export function matchExecutionFilter(task, filter = {}) {
  const query = text(filter.q).toLowerCase();
  if (query) {
    const haystack = [task.title, task.category, task.status, task.zone, task.zone_2, task.zone_3, task.device_type, task.comments, task.done_by]
      .map(text).join(' ').toLowerCase();
    if (!haystack.includes(query)) return false;
  }
  if (filter.status?.length && !filter.status.includes(text(task.status) || 'Not Started')) return false;
  if (filter.category?.length && !filter.category.includes(text(task.category))) return false;
  if (filter.building?.length && !filter.building.includes(text(task.zone))) return false;
  if (filter.doneBy?.length) {
    const people = splitPeople(task.done_by);
    const wantsNobody = filter.doneBy.includes('none');
    const wanted = new Set(filter.doneBy.filter((name) => name !== 'none').map(personKey));
    if (!(people.some((name) => wanted.has(personKey(name))) || (wantsNobody && !people.length))) return false;
  }
  if (filter.monitoring === 'on' && !task.monitoring) return false;
  if (filter.monitoring === 'off' && task.monitoring) return false;
  if (filter.comments && !text(task.comments)) return false;
  return true;
}

/**
 * Tasks to show for a filter: the matches plus their parents (for context).
 * → { active, matched: Set, included: Set, openParents: Set (parents with a match below them) }
 */
export function filterExecutionTasks(tasks = [], filter = {}, tree = null) {
  const active = isExecutionFilterActive(filter);
  if (!active) return { active, matched: null, included: null, openParents: new Set() };
  const { byId } = tree || buildExecutionTree(tasks);
  const matched = new Set();
  const included = new Set();
  const openParents = new Set();
  (tasks || []).forEach((task) => {
    if (!matchExecutionFilter(task, filter)) return;
    matched.add(task.id);
    let current = task;
    let first = true;
    while (current) {
      included.add(current.id);
      if (!first) openParents.add(current.id);
      first = false;
      current = current.parent_id ? byId.get(current.parent_id) : null;
    }
  });
  return { active, matched, included, openParents };
}

const countBy = (tasks, read) => {
  const counts = new Map();
  tasks.forEach((task) => {
    read(task).forEach((value) => counts.set(value, (counts.get(value) || 0) + 1));
  });
  return counts;
};

/** Chip options with counts (of main tasks for Status / Category, of sub-items for Building). */
export function executionFilterOptions(tasks = []) {
  const main = tasks.filter((task) => Number(task.level || 1) === 1);
  const statusCounts = countBy(tasks, (task) => [text(task.status) || 'Not Started']);
  const categoryCounts = countBy(tasks, (task) => (text(task.category) ? [text(task.category)] : []));
  const buildingCounts = countBy(tasks, (task) => (text(task.zone) ? [text(task.zone)] : []));
  // One chip per person whatever the spelling, named by the spelling used most.
  const spellings = new Map();
  tasks.forEach((task) => splitPeople(task.done_by).forEach((name) => {
    const key = personKey(name);
    if (!spellings.has(key)) spellings.set(key, new Map());
    spellings.get(key).set(name, (spellings.get(key).get(name) || 0) + 1);
  }));
  const peopleCounts = new Map([...spellings.values()].map((byName) => {
    const [label] = [...byName.entries()].reduce((best, entry) => (entry[1] > best[1] ? entry : best));
    return [label, [...byName.values()].reduce((sum, count) => sum + count, 0)];
  }));
  const unassigned = main.filter((task) => !splitPeople(task.done_by).length).length;
  const natural = (a, b) => String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
  const toOptions = (counts, order = null) => {
    const keys = order ? order.filter((key) => counts.has(key)).concat([...counts.keys()].filter((key) => !order.includes(key)).sort(natural)) : [...counts.keys()].sort(natural);
    return keys.map((key) => ({ value: key, label: key, count: counts.get(key) }));
  };
  return {
    status: toOptions(statusCounts, EXECUTION_STATUS_OPTIONS),
    category: toOptions(categoryCounts, EXECUTION_CATEGORY_OPTIONS),
    building: toOptions(buildingCounts),
    doneBy: [
      ...toOptions(peopleCounts),
      ...(unassigned ? [{ value: 'none', label: 'Unassigned', count: unassigned, muted: true }] : []),
    ],
  };
}

/** Main tasks a view would show: the task itself or something under it matches. */
export function countMainTasksMatching(tasks = [], filter = {}, tree = null) {
  const { matched, active } = filterExecutionTasks(tasks, filter, tree);
  const main = tasks.filter((task) => Number(task.level || 1) === 1);
  if (!active) return main.length;
  const { rootOf } = tree || buildExecutionTree(tasks);
  const roots = new Set();
  matched.forEach((id) => {
    const root = rootOf.get(id);
    if (root) roots.add(root.id);
  });
  return roots.size;
}

/* ------------------------------------------------------------------ rows on screen (09 #9) */

/**
 * The rows the table shows, in tree order: roots, then the sub-items of every OPEN parent (closed parents
 * cost nothing — their sub-items are not even built). With a filter, only `included` tasks show.
 * Row objects are reused from `cache` (Map) while nothing they show changed, so React.memo rows skip
 * re-rendering. → [{ id, task, depth, title, hasChildren, expanded, childCount, progress, zones }]
 */
export function buildExecutionRows({ tree, collapsed = {}, included = null, cache = null }) {
  const rows = [];
  const progressOf = (children) => {
    const relevant = children.filter((child) => text(child.status).toLowerCase() !== 'not required');
    if (!relevant.length) return { done: 0, total: 0, notRequired: true };
    return { done: relevant.filter((child) => text(child.status) === 'Done').length, total: relevant.length, notRequired: false };
  };
  const visit = (task, depth) => {
    if (included && !included.has(task.id)) return;
    const children = tree.children.get(task.id) || [];
    const hasChildren = children.length > 0;
    const expanded = hasChildren && !collapsed[task.id];
    const progress = hasChildren ? progressOf(children) : null;
    const zones = hasChildren && !text(task.zone)
      ? new Set(children.map((child) => text(child.zone)).filter(Boolean)).size
      : 0;
    const title = formatExecutionTaskTitle(task, tree.byId);
    const previous = cache?.get(task.id);
    const unchanged = previous
      && previous.task === task
      && previous.depth === depth
      && previous.expanded === expanded
      && previous.childCount === children.length
      && previous.title === title
      && previous.zones === zones
      && (previous.progress?.done ?? -1) === (progress?.done ?? -1)
      && (previous.progress?.total ?? -1) === (progress?.total ?? -1);
    const row = unchanged ? previous : { id: task.id, task, depth, title, hasChildren, expanded, childCount: children.length, progress, zones };
    cache?.set(task.id, row);
    rows.push(row);
    if (expanded) children.forEach((child) => visit(child, depth + 1));
  };
  tree.roots.forEach((task) => visit(task, 0));
  return rows;
}

/* ------------------------------------------------------------------ bulk changes (09 #4) */

const DONE = 'Done';

/**
 * What setting `changes` ({ field: value }) on the selected tasks would do. Sub-items of a selected task
 * follow it (the same cascade as a parent edit).
 * → { affectedIds, changed (tasks that get a different value), subItems (changed ones that were not
 *     selected), overwritten (recorded work that would be replaced), overwrittenSummary }
 */
export function planBulkChange(tasks = [], selectedIds = [], changes = {}, tree = null) {
  const { children } = tree || buildExecutionTree(tasks);
  const selected = new Set(selectedIds);
  const affected = new Set();
  selectedIds.forEach((id) => {
    affected.add(id);
    getDescendantIds(tasks, id, children).forEach((childId) => affected.add(childId));
  });
  const fields = Object.keys(changes);
  const byId = new Map(tasks.map((task) => [task.id, task]));
  let changed = 0;
  let subItems = 0;
  let overwritten = 0;
  affected.forEach((id) => {
    const task = byId.get(id);
    if (!task) return;
    const differs = fields.filter((field) => !same(task[field], changes[field]));
    if (!differs.length) return;
    changed += 1;
    if (!selected.has(id)) subItems += 1;
    const replacesWork = differs.some((field) => {
      if (field === 'status') return task.status === DONE && changes.status !== DONE;
      if (['done_by', 'checked_by', 'start_date', 'due_date', 'comments'].includes(field)) return Boolean(text(task[field]));
      return false;
    });
    if (replacesWork && !selected.has(id)) overwritten += 1;
  });
  let overwrittenSummary = '';
  if (overwritten) {
    overwrittenSummary = fields.includes('status') && changes.status !== DONE
      ? `${plural(overwritten, 'sub-item')} ${overwritten === 1 ? 'is' : 'are'} already Done`
      : `${plural(overwritten, 'sub-item')} ${overwritten === 1 ? 'has' : 'have'} a different value recorded`;
  }
  return { affectedIds: [...affected], changed, subItems, overwritten, overwrittenSummary };
}

/**
 * Applies `changes` to the affected tasks (keeps object identity of the others), then re-syncs parent
 * statuses. → { next, previous: Map(id → { field: old value, status, updated }) } for an exact Undo.
 */
export function applyBulkChange(tasks = [], affectedIds = [], changes = {}, timestamp = new Date().toISOString()) {
  const affected = new Set(affectedIds);
  const fields = Object.keys(changes);
  let updated = tasks.map((task) => {
    if (!affected.has(task.id)) return task;
    if (fields.every((field) => same(task[field], changes[field]))) return task;
    return { ...task, ...changes, updated: timestamp };
  });
  if (fields.includes('status') && changes.status !== 'Done') updated = reopenDoneAncestors(updated, affectedIds, timestamp);
  const next = fields.includes('status') ? syncExecutionTaskHierarchy(updated, timestamp) : updated;
  const previous = new Map();
  next.forEach((task, index) => {
    const before = tasks[index];
    if (!before || before.id !== task.id) return;
    const statusMoved = before.status !== task.status;
    const touched = fields.some((field) => !same(before[field], task[field])) || statusMoved;
    if (!touched) return;
    // Only what this change replaced: a later status change on the same task must survive this Undo.
    const entry = { updated: before.updated };
    if (statusMoved) entry.status = before.status;
    fields.forEach((field) => { entry[field] = before[field]; });
    previous.set(task.id, entry);
  });
  return { next, previous };
}

/** Puts back exactly what applyBulkChange replaced (other edits made since stay). */
export function revertBulkChange(tasks = [], previous = new Map()) {
  return tasks.map((task) => (previous.has(task.id) ? { ...task, ...previous.get(task.id) } : task));
}

/** Selected tasks + their sub-items, in list order (what Export / Print of a selection include). */
export function expandSelection(tasks = [], selectedIds = [], tree = null) {
  const { children } = tree || buildExecutionTree(tasks);
  const wanted = new Set();
  selectedIds.forEach((id) => {
    wanted.add(id);
    getDescendantIds(tasks, id, children).forEach((childId) => wanted.add(childId));
  });
  return tasks.filter((task) => wanted.has(task.id));
}

/** Status counts of main tasks for the summary bar: [{ key, label, count, tone }] */
export function executionStatusBreakdown(tasks = []) {
  const main = tasks.filter((task) => Number(task.level || 1) === 1);
  const count = (statuses) => main.filter((task) => statuses.includes(text(task.status) || 'Not Started')).length;
  return [
    { key: 'done', label: 'Done', count: count(['Done']), tone: 'navy' },
    { key: 'progress', label: 'In progress', count: count(['In Progress']), tone: 'blue' },
    { key: 'waiting', label: 'Waiting', count: count(WAITING_STATUSES), tone: 'amber' },
    { key: 'blocked', label: 'Bottleneck', count: count(BLOCKED_STATUSES), tone: 'red' },
    { key: 'notStarted', label: 'Not started', count: count(['Not Started']), tone: 'gray' },
    { key: 'notRequired', label: 'Not required', count: count(['Not required']), tone: 'outline' },
  ];
}
